import { throttle } from 'lodash'
import React, { ReactNode, SyntheticEvent, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CreationToolsArtboardContext } from './context'
import { Draggable, ResizeHandle, RotateHandle, SelectedBorder } from './draggable-ui'
import {
    ArtboardBaseLayerProps,
    ArtboardCornerPosition,
    ArtboardDragBounds,
    ArtboardDragPosition,
    ArtboardDragType,
} from './types'
import { getOppositeCorner } from './utils'

interface Props extends ArtboardBaseLayerProps {
    className?: string
    children?: ReactNode
    width: number
    height: number
    selectable?: boolean
    lockAspectRatio?: boolean
    transformOrigin?: ArtboardCornerPosition
    bounds?: ArtboardDragBounds
    forceSelected?: boolean
    onUpdate?: (position: ArtboardDragPosition) => void
}

type DragStartPositionRef = {
    startX: number
    startY: number
    centerX: number
    centerY: number
    mouseX: number
    mouseY: number
    baseDistance: number
    baseDistanceX: number
    baseDistanceY: number
    scrollTop: number
    dragType: string
}

const CreationToolsArtboardDraggable = ({
    className,
    children,
    defaultX,
    defaultY,
    defaultRotation = 0,
    width,
    height,
    selectable = true,
    moveable = false,
    resizeable = false,
    rotateable = false,
    lockAspectRatio = true,
    forceSelected = false,
    transformOrigin,
    bounds,
    onUpdate,
}: Props) => {
    const { renderingContext, canvasScale } = useContext(CreationToolsArtboardContext)
    const dragStartPositionRef = useRef<DragStartPositionRef>()
    const draggableRef = useRef<HTMLDivElement>(null)
    const [focused, setFocused] = useState<boolean>(false)
    const [x, setX] = useState<number>(defaultX)
    const [y, setY] = useState<number>(defaultY)
    const [rotation, setRotation] = useState<number>(defaultRotation)
    const [scaleX, setScaleX] = useState<number>(1)
    const [scaleY, setScaleY] = useState<number>(1)
    const isInteractive = moveable || resizeable || rotateable
    const leftMultiplier = transformOrigin ? (transformOrigin.indexOf('right') > -1 ? 1 : 0) : 0.5
    const topMultiplier = transformOrigin ? (transformOrigin.indexOf('bottom') > -1 ? 1 : 0) : 0.5
    const canvasWidth = Math.round((renderingContext?.canvas.width || 1) / canvasScale)
    const canvasHeight = Math.round((renderingContext?.canvas.height || 1) / canvasScale)

    const throttledUpdateCallback = useCallback(
        throttle((position: ArtboardDragPosition) => {
            onUpdate && onUpdate(position)
        }, 50),
        [onUpdate]
    )

    const onMouseDown = (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => {
        if (!draggableRef.current) return

        const { dragType = ArtboardDragType.Move } = (e.target as HTMLDivElement).dataset
        const rect = draggableRef.current.getBoundingClientRect()
        const scrollTop = (document.scrollingElement || document.documentElement || document.body).scrollTop || 0

        // store drag start information
        dragStartPositionRef.current = {
            mouseX: e.nativeEvent.pageX,
            mouseY: e.nativeEvent.pageY - scrollTop,
            centerX: rect.left + rect.width / 2,
            centerY: rect.top + rect.height / 2,
            baseDistance: Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2)) / (transformOrigin ? 1 : 2),
            baseDistanceX: width / 2,
            baseDistanceY: height / 2,
            startX: x,
            startY: y,
            dragType,
            scrollTop,
        }

        // add mouse events
        document.onmousemove = onMouseMove
        document.onmouseup = onMouseUp
    }

    const onMouseMove = (e: MouseEvent) => {
        if (!dragStartPositionRef.current) return

        const {
            startX,
            startY,
            mouseX,
            mouseY,
            centerX,
            centerY,
            baseDistance,
            baseDistanceX,
            baseDistanceY,
            dragType,
            scrollTop,
        } = dragStartPositionRef.current

        // get difference in mouse movement
        const currentX = e.pageX
        const currentY = e.pageY - scrollTop
        const diffMouseX = currentX - mouseX
        const diffMouseY = currentY - mouseY
        const rotationFromCenter = (Math.atan2(centerX - currentX, centerY - currentY) * -180) / Math.PI
        const distanceFromCenter = Math.sqrt(Math.pow(centerX - currentX, 2) + Math.pow(centerY - currentY, 2))

        // move
        if (dragType === ArtboardDragType.Move && moveable) {
            const newX = startX + diffMouseX
            const newY = startY + diffMouseY
            const midX = (width * scaleX) / 2
            const midY = (height * scaleY) / 2
            setX(bounds ? Math.max(bounds.left + midX, Math.min(bounds.right - midX, newX)) : newX)
            setY(bounds ? Math.max(bounds.top + midY, Math.min(bounds.bottom - midY, newY)) : newY)
        }

        // rotate
        if (dragType === ArtboardDragType.Rotate && rotateable) {
            setRotation(rotationFromCenter)
        }

        // resize
        if (dragType === ArtboardDragType.Resize && resizeable) {
            let newScaleX = lockAspectRatio
                ? distanceFromCenter / baseDistance
                : Math.abs(centerX - currentX) / baseDistanceX
            let newScaleY = lockAspectRatio ? newScaleX : Math.abs(centerY - currentY) / baseDistanceY

            // TODO: Clean up the transformOrigin code
            if (transformOrigin) {
                newScaleX += 0.5
                newScaleY += 0.5
            }

            // TODO: Clean up this bounds stuff
            if (bounds) {
                // limit scale to bounds
                const maxScaleX = bounds ? (bounds.right - bounds.left) / width : 99999
                const maxScaleY = bounds ? (bounds.bottom - bounds.top) / height : 99999
                newScaleX = lockAspectRatio
                    ? Math.min(newScaleX, newScaleY, maxScaleX, maxScaleY)
                    : Math.min(newScaleX, maxScaleX)
                newScaleY = lockAspectRatio ? newScaleX : Math.min(newScaleY, maxScaleY)

                // adjust x/y
                const newMidX = (width * newScaleX) / 2
                const newMidY = (height * newScaleY) / 2
                setX(Math.max(bounds.left + newMidX, Math.min(bounds.right - newMidX, startX)))
                setY(Math.max(bounds.top + newMidY, Math.min(bounds.bottom - newMidY, startY)))
            }

            setScaleX(newScaleX)
            setScaleY(newScaleY)
        }
    }

    const onMouseUp = () => {
        // remove mouse events
        document.onmousemove = null
        document.onmouseup = null
    }

    useEffect(() => {
        throttledUpdateCallback({ x, y, rotation, scaleX, scaleY })
    }, [x, y, rotation, scaleX, scaleY])

    return (
        <Draggable
            {...{
                ref: draggableRef,
                tabIndex: 0,
                style: {
                    width: `${((width * scaleX) / canvasWidth) * 100}%`,
                    height: `${((height * scaleY) / canvasHeight) * 100}%`,
                    left: `${((x - width * scaleX * leftMultiplier) / canvasWidth) * 100}%`,
                    top: `${((y - height * scaleY * topMultiplier) / canvasHeight) * 100}%`,
                    transform: `rotate(${rotation}deg)`,
                    pointerEvents: !isInteractive ? 'none' : undefined,
                },
                selected: forceSelected || focused,
                onFocus: selectable ? () => setFocused(true) : undefined,
                onBlur: selectable ? () => setFocused(false) : undefined,
                onMouseDown,
                className,
            }}
        >
            {children}
            {(forceSelected || focused) && (
                <>
                    <SelectedBorder data-drag-type={ArtboardDragType.Move} />
                    {rotateable && <RotateHandle data-drag-type={ArtboardDragType.Rotate} />}
                    {resizeable && (
                        <ResizeHandle
                            position={
                                transformOrigin
                                    ? getOppositeCorner(transformOrigin)
                                    : ArtboardCornerPosition.BottomRight
                            }
                            data-drag-type={ArtboardDragType.Resize}
                        />
                    )}
                </>
            )}
        </Draggable>
    )
}

export default CreationToolsArtboardDraggable
